import {
  DeliveryTask,
  IssueAmountStructure,
  IssueCase,
  IssueCaseCategory,
  IssueCaseDoc,
  IssueCaseIssuedByType,
  IssueCaseRequestCategory,
  IssueCaseRequestSubcategory,
  IssueCaseStatusCode,
  IssueCaseSubcategory,
  IssueLiability,
  IssueLiabilityCostType,
  IssueLiabilityDoc,
  IssueLiabilityHistoryEntry,
  IssueLiabilityStatus,
  IssueProduct,
  IssueSourceType,
  IssueTaskCategory,
  IssueTaskDeadlineOption,
  IssueTaskDoc,
  IssueTaskStatus,
  OrderProduct,
  PickupTask,
  TaskItemStatus,
  issueCaseCategoryMap,
  issueCaseRequestCategoryMap,
  issueCaseRequestSubcategoryMap,
  issueCaseSubcategoryMap,
  issueLiabilityStatusMap,
  issueTaskCategoryMap,
} from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { addDays } from 'date-fns';
import { get } from 'lodash-es';

import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import {
  addProductToIssueCase,
  batchEnd,
  batchStart,
  getDeliveryTask,
  getIssueLiabilityByCaseId,
  getPickupTask,
  removeProductFromIssueCase,
  setIssueLiability,
  updateDeliveryTask,
  updateIssueCase,
  updateIssueLiability,
  updateIssueTask,
  updatePickupTask,
} from 'src/utils/firebase/firebase-short-cut';

import { getCategoryLabel, getSubcategoryLabel } from './issue-data-util';

export const issueCaseOptions = Object.entries(issueCaseCategoryMap).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const issueCaseRequestOptions = Object.entries(issueCaseRequestCategoryMap).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const issueTaskOptions = Object.entries(issueTaskCategoryMap).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const issueCaseSubOptions = (category: IssueCaseCategory | IssueCaseRequestCategory) => {
  const issueMap = issueCaseSubcategoryMap[category as keyof typeof issueCaseSubcategoryMap];
  if (issueMap) {
    return Object.entries(issueMap).map(([key, value]) => ({
      label: value,
      value: key,
    }));
  }
  const requestMap = issueCaseRequestSubcategoryMap[category as IssueCaseRequestCategory];
  if (requestMap) {
    return Object.entries(requestMap).map(([key, value]) => ({
      label: value as string,
      value: key,
    }));
  }
  return null;
};

/** 이슈 제기 유형 한글 매핑 */
export const issueCaseIssuedByMap: Record<IssueCaseIssuedByType, string> = {
  customer: '👤 고객',
  partner: '🚚 파트너',
  manager: '👨‍💻 관리자',
  other: '※ 기타',
};

/** 이슈 제기 유형 옵션 (Select용) */
export const issueCaseIssuedByOptions = Object.entries(issueCaseIssuedByMap).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const issueTaskStatusBadgeMap: Record<
  IssueTaskStatus,
  {
    text: string;
    status: 'success' | 'processing' | 'warning' | 'error' | 'default';
  }
> = {
  [IssueTaskStatus.PENDING]: {
    text: '등록 대기',
    status: 'default',
  },
  [IssueTaskStatus.OPEN]: {
    text: '등록 완료',
    status: 'warning',
  },
  [IssueTaskStatus.IN_PROGRESS]: {
    text: '처리 중',
    status: 'processing',
  },
  [IssueTaskStatus.COMPLETED]: {
    text: '완료',
    status: 'success',
  },
  [IssueTaskStatus.FAILED]: {
    text: '실패',
    status: 'error',
  },
  [IssueTaskStatus.CANCELED]: {
    text: '취소',
    status: 'default',
  },
  [IssueTaskStatus.DELETED]: {
    text: '삭제',
    status: 'default',
  },
};

export type IssueCaseBase = Pick<
  IssueCase,
  | 'sourceType'
  | 'sourceId'
  | 'sourceReferenceDate'
  | 'storeId'
  | 'storeName'
  | 'creatorId'
  | 'creatorType'
  | 'category'
  | 'subcategory'
> &
  Partial<Pick<IssueCase, 'caseKind' | 'content'>>;

function getIssuedByFromSubcategory(
  subcategory: IssueCaseSubcategory | IssueCaseRequestSubcategory | null,
): 'customer' | 'manager' {
  switch (subcategory) {
    case IssueCaseSubcategory.ORDER_ADJUSTMENT:
      return 'manager';
    default:
      return 'customer';
  }
}

export const getIssueCaseInitialData = (base: IssueCaseBase): IssueCase => {
  const issuedBy = getIssuedByFromSubcategory(base.subcategory);
  return {
    status: IssueCaseStatusCode.OPEN,
    registeredAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
    sourceType: base.sourceType,
    sourceId: base.sourceId,
    sourceReferenceDate: base.sourceReferenceDate,
    storeId: base.storeId,
    storeName: base.storeName,
    creatorId: base.creatorId,
    creatorType: base.creatorType,
    category: base.category,
    subcategory: base.subcategory,
    caseKind: base.caseKind ?? 'issue',
    content: base.content ?? null,
    issuedBy,
    publicNote: null,
    privateNote: null,
    amount: null,
    images: null,
    products: null,
    bigquerySavedAt: null,
    linkedMessageStats: null,
  };
};

export const needDeadlineSetting = (category: IssueTaskCategory) =>
  ![IssueTaskCategory.QUICK, IssueTaskCategory.INTERNAL].includes(category);
export const needDeliveryInfoSetting = (category: IssueTaskCategory) => IssueTaskCategory.DELIVERY === category;
export const needPickupInfoSetting = (category: IssueTaskCategory) => IssueTaskCategory.PICK_UP === category;
export const needPublicNoteSetting = (category: IssueTaskCategory) => IssueTaskCategory.INTERNAL !== category;
export const needInvoiceInfoSetting = (category: IssueTaskCategory) =>
  [IssueTaskCategory.REFUND, IssueTaskCategory.ADDITIONAL_CHARGE].includes(category);
export const needInventoryIssueInfoSetting = (category: IssueTaskCategory) =>
  [IssueTaskCategory.PICK_UP].includes(category);

export const issueCaseStatusBadgeMap: Record<
  IssueCaseStatusCode,
  {
    text: string;
    status: 'success' | 'processing' | 'warning' | 'error' | 'default';
  }
> = {
  [IssueCaseStatusCode.PENDING]: {
    text: '등록 대기',
    status: 'default',
  },
  [IssueCaseStatusCode.OPEN]: {
    text: '등록 완료',
    status: 'warning',
  },
  [IssueCaseStatusCode.IN_PROGRESS]: {
    text: '처리 중',
    status: 'processing',
  },
  [IssueCaseStatusCode.CLOSED]: {
    text: '완료',
    status: 'success',
  },
  [IssueCaseStatusCode.CANCELED]: {
    text: '취소',
    status: 'default',
  },
  [IssueCaseStatusCode.DELETED]: {
    text: '삭제',
    status: 'error',
  },
};

export const issueLiabilityStatusBadgeMap: Record<
  IssueLiabilityStatus,
  {
    text: string;
    status: 'success' | 'processing' | 'warning' | 'error' | 'default';
  }
> = {
  registered: {
    text: issueLiabilityStatusMap.registered,
    status: 'processing',
  },
  delivered: {
    text: issueLiabilityStatusMap.delivered,
    status: 'warning',
  },
  settled: {
    text: issueLiabilityStatusMap.settled,
    status: 'success',
  },
  unsettled: {
    text: issueLiabilityStatusMap.unsettled,
    status: 'error',
  },
  closed: {
    text: issueLiabilityStatusMap.closed,
    status: 'default',
  },
};

export const getIssueCaseSubcategories = (category: IssueCaseCategory | IssueCaseRequestCategory) => {
  const issueMap = issueCaseSubcategoryMap[category as keyof typeof issueCaseSubcategoryMap];
  if (issueMap) return issueMap;
  const requestMap = issueCaseRequestSubcategoryMap[category as IssueCaseRequestCategory];
  return requestMap ?? null;
};

export const parseOrderProductsToIssueProduct = (
  orderProduct: Omit<OrderProduct, 'snapshotId' | 'snapshotPrice' | 'priceDiff'> & { snapshotPrice?: number | null },
) => {
  const productId = orderProduct.productId;
  const productName = orderProduct.fullName;
  const inventoryId = orderProduct.inventoryId;
  const inventoryUnit = orderProduct.inventoryUnit;
  const taxFree = orderProduct.taxFree;
  const price = orderProduct.snapshotPrice ?? orderProduct.price;
  const quantity = orderProduct.volume;
  const { totalAmount, taxAmount, supplyAmount } = calculateIssueProductAmount(price, quantity, taxFree);
  return {
    productId,
    productName,
    inventoryId,
    inventoryUnit,
    detail: null,
    taxFree,
    price,
    quantity,
    amount: { totalAmount, taxAmount, supplyAmount },
  };
};

export const parseOrderProductsToIssueProducts = (orderProducts: OrderProduct[]) => {
  return orderProducts.map(parseOrderProductsToIssueProduct);
};

export const onAddProductToIssueCase = async (issueCaseDoc: IssueCaseDoc, issueProduct: IssueProduct) => {
  const isDuplicate = issueCaseDoc.products?.some((p) => p.productId === issueProduct.productId);
  if (isDuplicate) {
    message.error('이미 추가된 상품입니다.');
    return;
  }
  try {
    const newProducts = [...(issueCaseDoc.products ?? []), issueProduct];
    const amount = calculateIssueAmount(newProducts);
    await addProductToIssueCase(issueCaseDoc._id, issueProduct, amount);
  } catch (error) {
    console.error(error);
    message.error('상품 추가에 실패했습니다.');
  }
};

export const onRemoveProductFromIssueCase = async (issueCaseDoc: IssueCaseDoc, issueProduct: IssueProduct) => {
  const isExist = issueCaseDoc.products?.some((p) => p.productId === issueProduct.productId);
  if (!isExist) {
    message.error('상품을 찾을 수 없습니다.');
    return;
  }
  try {
    const newProducts = issueCaseDoc.products?.filter((p) => p.productId !== issueProduct.productId);
    const amount = newProducts ? calculateIssueAmount(newProducts) : null;
    await removeProductFromIssueCase(issueCaseDoc._id, issueProduct, amount);
  } catch (error) {
    console.error(error);
    message.error('상품 삭제에 실패했습니다.');
  }
};

/**
 * 이슈 상품의 가격을 기준으로 세금 항목을 계산한다.
 * - price는 taxFree가 false인 경우 부가세 10%가 포함된 가격
 * - taxFree가 true인 경우 price가 그대로 공급가가 됨
 * @param price 상품 가격 (부가세 포함 또는 면세)
 * @param quantity 수량
 * @param taxFree 면세 여부
 * @returns { totalAmount: 총 금액, taxAmount: 세금 금액, supplyAmount: 공급가 금액 }
 */
export const calculateIssueProductAmount = (
  price: number,
  quantity: number,
  taxFree: boolean,
): { totalAmount: number; taxAmount: number; supplyAmount: number } => {
  // 면세 상품인 경우
  if (taxFree) {
    // 소수점 이하 금액 절삭
    const supplyAmount = Math.floor(price * quantity);
    return {
      totalAmount: supplyAmount, // 면세 상품은 총 금액 = 공급가
      taxAmount: 0,
      supplyAmount: supplyAmount,
    };
  }

  // 과세 상품인 경우: price에서 부가세를 역산
  // 1. 개별 상품의 공급가 계산 (부가세 제외)
  const supplyPrice = Math.round(price / 1.1);

  // 2. 개별 상품의 부가세 계산
  const taxPrice = price - supplyPrice;

  // 3. 수량을 곱해서 최종 금액들 계산 (소수점 이하 금액 절삭)
  const supplyAmount = Math.floor(supplyPrice * quantity);
  const taxAmount = Math.floor(taxPrice * quantity);
  const totalAmount = supplyAmount + taxAmount;

  return {
    totalAmount,
    taxAmount,
    supplyAmount,
  };
};

export const calculateIssueAmount = (products: IssueProduct[]) => {
  return products.reduce(
    (acc, cur) => {
      return {
        totalAmount: acc.totalAmount + (cur.amount?.totalAmount ?? 0),
        taxAmount: acc.taxAmount + (cur.amount?.taxAmount ?? 0),
        supplyAmount: acc.supplyAmount + (cur.amount?.supplyAmount ?? 0),
      };
    },
    { totalAmount: 0, taxAmount: 0, supplyAmount: 0 },
  );
};

interface ProductUpdateTarget {
  _id: string;
  products?: IssueProduct[] | null;
  amount?: IssueAmountStructure | null;
}

export const onUpdateProductQuantity = async <T extends ProductUpdateTarget>(
  doc: T,
  productId: string,
  quantity: number,
  updateFn: (id: string, data: Partial<T>) => Promise<string>,
) => {
  if (quantity === 0) {
    message.error('수량은 0이 될 수 없습니다.');
    return;
  }

  const product = doc.products?.find((p) => p.productId === productId);
  if (!product) {
    message.error('상품을 찾을 수 없습니다.');
    return;
  }

  const newProducts = doc.products?.map((p) => {
    if (p.productId === productId) {
      const { totalAmount, taxAmount, supplyAmount } = calculateIssueProductAmount(p.price, quantity, p.taxFree);
      return {
        ...p,
        quantity,
        amount: { totalAmount, taxAmount, supplyAmount },
      };
    }
    return p;
  });

  try {
    const amount = newProducts ? calculateIssueAmount(newProducts) : null;
    await updateFn(doc._id, { products: newProducts, amount } as Partial<T>);
  } catch (error) {
    console.error(error);
    message.error('상품 수량 변경에 실패했습니다.');
  }
};

export const onUpdateIssueCaseProductQuantity = (issueCaseDoc: IssueCaseDoc, productId: string, quantity: number) =>
  onUpdateProductQuantity(issueCaseDoc, productId, quantity, updateIssueCase);

export const onUpdateIssueTaskProductQuantity = (issueTaskDoc: IssueTaskDoc, productId: string, quantity: number) =>
  onUpdateProductQuantity(issueTaskDoc, productId, quantity, updateIssueTask);

export const getIssueCaseLabel = (issueCaseDoc: Pick<IssueCaseDoc, 'category' | 'subcategory'>) => {
  const category = getCategoryLabel(issueCaseDoc.category);
  if (issueCaseDoc.subcategory) {
    const subcategory = getSubcategoryLabel(issueCaseDoc.category, issueCaseDoc.subcategory);
    return `${category} / ${subcategory}`;
  }
  return category;
};

export const getIssueTaskLabel = (issueTaskDoc: Pick<IssueTaskDoc, 'category'>) => {
  return issueTaskCategoryMap[issueTaskDoc.category];
};

export const getIssueTaskDeadlineInitialValue = (option: IssueTaskDeadlineOption) => {
  switch (option) {
    case IssueTaskDeadlineOption.ASAP:
      return null;
    case IssueTaskDeadlineOption.BEFORE_DATE:
      return formatDate(addDays(new Date(), 1), 'yyyy-MM-dd');
    case IssueTaskDeadlineOption.SPECIFIC_DATE:
      return formatDate(addDays(new Date(), 1), 'yyyy-MM-dd');
  }
};

export const validateIssueCaseStatusForChange = (
  currentStatus: IssueCaseStatusCode,
  newStatus: IssueCaseStatusCode,
) => {
  if (currentStatus === newStatus) {
    return {
      isValid: false,
      errorMessage: '현재 이슈 상태와 동일한 상태로 변경할 수 없습니다.',
    };
  }
  if (currentStatus >= IssueCaseStatusCode.IN_PROGRESS) {
    if (newStatus < IssueCaseStatusCode.IN_PROGRESS) {
      return {
        isValid: false,
        errorMessage: '처리중인 경우 등록대기, 등록완료 상태로 변경할 수 없습니다.',
      };
    }
  }
  return {
    isValid: true,
    errorMessage: null,
  };
};

export const validateIssueTaskStatusForChange = (currentStatus: IssueTaskStatus, newStatus: IssueTaskStatus) => {
  if (currentStatus === newStatus) {
    return {
      isValid: false,
      errorMessage: '현재 이슈 상태와 동일한 상태로 변경할 수 없습니다.',
    };
  }

  // COMPLETED 상태에서는 CANCELED로만 변경 가능
  if (currentStatus === IssueTaskStatus.COMPLETED) {
    if (newStatus !== IssueTaskStatus.CANCELED) {
      return {
        isValid: false,
        errorMessage: '완료 상태에서는 취소 상태로만 변경할 수 있습니다.',
      };
    }
  }

  // COMPLETED 상태로 변경하려면 현재 상태가 IN_PROGRESS여야 함
  if (newStatus === IssueTaskStatus.COMPLETED) {
    if (currentStatus !== IssueTaskStatus.IN_PROGRESS) {
      return {
        isValid: false,
        errorMessage: '완료 상태로 변경하려면 현재 상태가 처리중이어야 합니다.',
      };
    }
  }

  if (currentStatus >= IssueTaskStatus.IN_PROGRESS) {
    if (newStatus < IssueTaskStatus.IN_PROGRESS) {
      return {
        isValid: false,
        errorMessage: '처리중인 경우 등록대기, 등록완료 상태로 변경할 수 없습니다.',
      };
    }
  }
  return {
    isValid: true,
    errorMessage: null,
  };
};

export const isCaseClosable = (status: IssueCaseStatusCode, issueTasks: IssueTaskDoc[]) => {
  // 완료 상태의 태스크가 없다면 완료로 처리할지 물어본다.
  return status === IssueCaseStatusCode.CLOSED && issueTasks.every((task) => task.status >= IssueTaskStatus.FAILED);
};

/**
 * IssueTask들 중에 PENDING 상태인 것이 하나 이상 존재하는지 확인
 */
export const hasPendingTasks = (tasksByCase: Record<string, IssueTaskDoc[]>) => {
  const pendingTasksCount = Object.values(tasksByCase)
    .flat()
    .filter((task) => task.status === IssueTaskStatus.PENDING).length;

  return {
    hasPending: pendingTasksCount > 0,
    pendingTasksCount,
  };
};

/**
 * IssueTask들 중에 OPEN 상태인 것이 하나 이상 존재하는지 확인
 */
export const hasOpenTasks = (tasksByCase: Record<string, IssueTaskDoc[]>) => {
  const openTasksCount = Object.values(tasksByCase)
    .flat()
    .filter((task) => task.status === IssueTaskStatus.OPEN).length;

  return {
    hasOpen: openTasksCount > 0,
    openTasksCount,
  };
};

/**
 * IssueTask들을 일괄로 OPEN 상태로 변경
 */
export const batchOpenIssueTasks = async (tasksByCase: Record<string, IssueTaskDoc[]>) => {
  try {
    batchStart();

    // PENDING 상태인 IssueTask들을 OPEN으로 변경
    const allTasks = Object.values(tasksByCase).flat();
    const pendingTasks = allTasks.filter((task) => task.status === IssueTaskStatus.PENDING);
    for (const task of pendingTasks) {
      await updateIssueTask(task._id, { status: IssueTaskStatus.OPEN }, { bBatch: true });
    }

    await batchEnd();
    message.success(`${pendingTasks.length}개의 이슈 태스크가 등록되었습니다.`);
  } catch (error) {
    console.error(error);
    message.error('일괄 등록에 실패했습니다.');
  }
};

/**
 * IssueTask들을 일괄로 PENDING 상태로 되돌리기 (등록 취소)
 */
export const batchPendingIssueTasks = async (tasksByCase: Record<string, IssueTaskDoc[]>) => {
  try {
    batchStart();

    // OPEN 상태인 IssueTask들을 PENDING으로 변경
    const allTasks = Object.values(tasksByCase).flat();
    const openTasks = allTasks.filter((task) => task.status === IssueTaskStatus.OPEN);
    for (const task of openTasks) {
      await updateIssueTask(task._id, { status: IssueTaskStatus.PENDING }, { bBatch: true });
    }

    await batchEnd();
    message.success(`${openTasks.length}개의 이슈 태스크가 등록 취소되었습니다.`);
  } catch (error) {
    console.error(error);
    message.error('일괄 등록 취소에 실패했습니다.');
  }
};

/**
 * Partners task의 items를 일괄 상태 변경한 결과를 반환
 */
function buildUpdatedItems<T extends { itemStatus: TaskItemStatus }>(
  items: Record<string, T>,
  newItemStatus: TaskItemStatus,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(items).map(([key, item]) => [key, { ...item, itemStatus: newItemStatus }]),
  ) as Record<string, T>;
}

/**
 * IssueTask 완료/실패/취소 시 연관된 Partners task(deliveryTask/pickupTask)를 자동 동기화
 * - COMPLETED -> finishedAt 설정, 모든 items를 Completed로
 * - FAILED/CANCELED -> finishedAt 설정, cancelReason 설정, 모든 items를 Canceled로
 * - Partners task가 이미 완료(finishedAt !== null)된 경우 건너뜀
 * - bBatch=true로 호출하여 상위 배치에 포함
 */
async function syncPartnersTaskOnFinish(issueTaskDoc: IssueTaskDoc, newStatus: IssueTaskStatus): Promise<void> {
  const { category, deliveryInfo, pickupInfo, privateNote } = issueTaskDoc;
  const now = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
  const itemStatus = newStatus === IssueTaskStatus.COMPLETED ? TaskItemStatus.Completed : TaskItemStatus.Canceled;
  const cancelReason = newStatus !== IssueTaskStatus.COMPLETED ? `[콘솔 취소] ${privateNote ?? ''}` : undefined;

  if (category === IssueTaskCategory.DELIVERY && deliveryInfo?.partnersTaskId) {
    const task = await getDeliveryTask(deliveryInfo.partnersTaskId);
    if (!task) {
      message.warning('연관된 배송 Task를 찾을 수 없습니다. Partners 동기화를 건너뜁니다.');
      return;
    }
    if (task.finishedAt !== null) return;

    const updateData: Partial<DeliveryTask> = {
      finishedAt: now,
      deliveryItems: buildUpdatedItems(task.deliveryItems, itemStatus),
      ...(cancelReason && { cancelReason }),
    };
    await updateDeliveryTask(deliveryInfo.partnersTaskId, updateData, { bBatch: true });
  }

  if (category === IssueTaskCategory.PICK_UP && pickupInfo?.partnersTaskId) {
    const task = await getPickupTask(pickupInfo.partnersTaskId);
    if (!task) {
      message.warning('연관된 픽업 Task를 찾을 수 없습니다. Partners 동기화를 건너뜁니다.');
      return;
    }
    if (task.finishedAt !== null) return;

    const updateData: Partial<PickupTask> = {
      finishedAt: now,
      pickupItems: buildUpdatedItems(task.pickupItems, itemStatus),
      ...(cancelReason && { cancelReason }),
    };
    await updatePickupTask(pickupInfo.partnersTaskId, updateData, { bBatch: true });
  }
}

/** Task가 실질적으로 진행 단계에 진입한 상태 (Case를 IN_PROGRESS로 끌어올려야 하는 상태들) */
const TASK_STATUSES_TRIGGERING_CASE_PROGRESS: IssueTaskStatus[] = [
  IssueTaskStatus.IN_PROGRESS,
  IssueTaskStatus.COMPLETED,
  IssueTaskStatus.FAILED,
];

/** Task가 종료된 상태 (Partners task 동기화가 필요한 상태들) */
const TASK_FINISHED_STATUSES: IssueTaskStatus[] = [
  IssueTaskStatus.COMPLETED,
  IssueTaskStatus.FAILED,
  IssueTaskStatus.CANCELED,
];

/**
 * IssueTask 상태 변경 시 부모 IssueCase의 상태를 자동으로 업데이트
 * - Task가 IN_PROGRESS/COMPLETED/FAILED로 변경될 때, Case가 IN_PROGRESS 미만이면 Case도 IN_PROGRESS로 변경
 * - Task가 IN_PROGRESS에서 COMPLETED/FAILED/CANCELED로 변경될 때, Partners task도 자동 동기화
 */
export const updateIssueTaskWithCaseSync = async (
  issueTaskDoc: IssueTaskDoc,
  taskData: Partial<IssueTaskDoc>,
  caseId: string,
  currentCaseStatus: IssueCaseStatusCode,
) => {
  try {
    batchStart();

    await updateIssueTask(issueTaskDoc._id, taskData, { bBatch: true });

    // Case가 아직 IN_PROGRESS 미만이면 자동으로 IN_PROGRESS로 변경
    if (
      taskData.status &&
      TASK_STATUSES_TRIGGERING_CASE_PROGRESS.includes(taskData.status) &&
      currentCaseStatus < IssueCaseStatusCode.IN_PROGRESS
    ) {
      await updateIssueCase(caseId, { status: IssueCaseStatusCode.IN_PROGRESS }, { bBatch: true });
      message.info('연관된 이슈 케이스도 처리중 상태로 변경되었습니다.');
    }

    // IN_PROGRESS에서 종료 상태로 변경 시 Partners task 동기화
    const isTransitioningFromInProgress =
      issueTaskDoc.status === IssueTaskStatus.IN_PROGRESS &&
      taskData.status &&
      TASK_FINISHED_STATUSES.includes(taskData.status);

    if (isTransitioningFromInProgress && taskData.status) {
      await syncPartnersTaskOnFinish(issueTaskDoc, taskData.status);
    }

    await batchEnd();
  } catch (error) {
    console.error(error);
    message.error('이슈 태스크 상태 변경에 실패했습니다.');
  }
};

export const isIssueCaseEditable = (issueCaseDoc: IssueCaseDoc) => {
  return issueCaseDoc.status <= IssueCaseStatusCode.IN_PROGRESS;
};

export const isIssueTaskEditable = (issueTaskDoc: IssueTaskDoc) => {
  return issueTaskDoc.status <= IssueTaskStatus.IN_PROGRESS;
};

/**
 * 기존 IssueLiability가 있으면 반환, 없으면 새로 생성
 * - ag-grid 셀 편집 시 사용
 * - 내부적으로 createIssueLiability 활용
 */
export const getIssueLiabilityInitialData = (
  caseId: string,
  issueLiability: IssueLiabilityDoc | undefined,
): IssueLiability & { _id: string } => {
  if (issueLiability?._id) {
    return issueLiability;
  }

  const newLiability = createIssueLiability({ issueCaseId: caseId });
  return {
    _id: getIssueLiabilityByCaseId(),
    ...newLiability,
  };
};

export const onUpdateIssueLiabilityResponsibleType = async (params: any) => {
  const field = params.colDef.field;
  if (!field) {
    message.error(`수정할 수 없는 필드정보입니다. field: ${field as string}`);
    return;
  }

  const id: string = params.data._id;
  if (!id) {
    message.error('id값이 없습니다.');
    return;
  }
  const newValue = get(params.data, field);
  if (!newValue) {
    message.error('값을 입력해주세요.');
    return;
  }

  const initialData = getIssueLiabilityInitialData(id, params.data.issueLiability);
  const newData = {
    ...initialData,
    responsibleType: newValue,
    responsibleId: null,
  };

  try {
    await setIssueLiability(initialData._id, newData);
    message.success('변경완료');
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    message.error(msg ?? '알 수 없는 에러 발생!');
  }
};

export const onUpdateIssueLiabilityAmount = async (params: any) => {
  const field = params.colDef.field;
  if (!field) {
    message.error(`수정할 수 없는 필드정보입니다. field: ${field as string}`);
    return;
  }
  const id: string = params.data.issueLiability._id;
  if (!id) {
    message.error('id값이 없습니다.');
    return;
  }
  const amount = get(params.data, 'issueLiability.amount');
  if (!amount) {
    message.error('값을 입력해주세요.');
    return;
  }
  try {
    const totalAmount = amount.supplyAmount + amount.taxAmount;
    await updateIssueLiability(id, {
      amount: { totalAmount, supplyAmount: amount.supplyAmount, taxAmount: amount.taxAmount },
    });
    message.success('변경완료');
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    message.error(msg ?? '알 수 없는 에러 발생!');
  }
};

export const onUpdateIssueLiabilityMemo = async (params: any) => {
  const id: string = params.data.issueLiability._id;
  if (!id) {
    message.error('id값이 없습니다.');
    return;
  }
  const memo = get(params.data, 'issueLiability.memo');
  try {
    await updateIssueLiability(id, { memo });
    message.success('변경완료');
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    message.error(msg ?? '알 수 없는 에러 발생!');
  }
};

/** 상태 Badge 색상 매핑 */
export const issueStatusColorMap: Record<string, string> = {
  success: '#52c41a',
  processing: '#1890ff',
  warning: '#faad14',
  error: '#ff4d4f',
  default: '#8c8c8c',
};

/** 태스크 카테고리 색상 */
export const issueTaskCategoryColorMap: Record<IssueTaskCategory, string> = {
  [IssueTaskCategory.PICK_UP]: '#13c2c2',
  [IssueTaskCategory.DELIVERY]: '#1890ff',
  [IssueTaskCategory.QUICK]: '#fa8c16',
  [IssueTaskCategory.REFUND]: '#eb2f96',
  [IssueTaskCategory.ADDITIONAL_CHARGE]: '#722ed1',
  [IssueTaskCategory.OTHER]: '#8c8c8c',
  [IssueTaskCategory.INTERNAL]: '#595959',
  [IssueTaskCategory.REQUEST]: '#0f766e',
};

/**
 * IssuePanel 모달에 전달할 데이터를 생성하는 유틸리티 함수
 * sourceType/sourceId를 기반으로 적절한 IssuePanelProps 구조를 반환
 */
export type IssuePanelModalData = { orderId: string } | { issueId: string } | { standaloneIssueId: string } | null;

export const buildIssuePanelModalData = (
  issueId: string,
  sourceType: IssueSourceType | null,
  sourceId: string | null,
): IssuePanelModalData => {
  if (sourceType === IssueSourceType.ORDER && sourceId) {
    return { orderId: sourceId };
  } else if (sourceType === IssueSourceType.ISSUE && sourceId) {
    return { issueId: sourceId };
  } else {
    return { standaloneIssueId: issueId };
  }
};

/**
 * 새 IssueLiability 생성을 위한 초기 데이터 생성
 * - 모든 파라미터는 optional이며 기본값 제공
 * - createdBy가 있으면 생성 히스토리 엔트리 포함, 없으면 빈 배열
 */
export interface CreateIssueLiabilityParams {
  issueCaseId: string;
  responsibleType?: IssueLiability['responsibleType'];
  responsibleId?: string | null;
  amount?: {
    supplyAmount: number;
    taxAmount: number;
  };
  memo?: string | null;
  progressNote?: string | null;
  status?: IssueLiabilityStatus;
  costType?: IssueLiabilityCostType;
  /** 있으면 생성 히스토리 포함, 없으면 빈 히스토리 */
  createdBy?: string;
}

export const createIssueLiability = (params: CreateIssueLiabilityParams): IssueLiability => {
  const now = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
  const supplyAmount = params.amount?.supplyAmount ?? 0;
  const taxAmount = params.amount?.taxAmount ?? 0;
  const totalAmount = supplyAmount + taxAmount;

  const history: IssueLiabilityHistoryEntry[] = params.createdBy
    ? [{ createdAt: now, memo: '생성', createdBy: params.createdBy, isManual: false }]
    : [];

  return {
    registeredAt: now,
    issueCaseId: params.issueCaseId,
    responsibleType: params.responsibleType ?? 'etc',
    responsibleId: params.responsibleId ?? null,
    amount: { supplyAmount, taxAmount, totalAmount },
    memo: params.memo ?? null,
    progressNote: params.progressNote ?? null,
    deletedAt: null,
    status: params.status ?? IssueLiabilityStatus.registered,
    costType: params.costType ?? IssueLiabilityCostType.general,
    bigquerySavedAt: null,
    history,
  };
};

/**
 * IssueLiability 상태 변경 시 히스토리 자동 추가
 * - 상태가 변경된 경우에만 히스토리에 기록
 * - 모든 상태 변경은 이 함수를 통해 수행하여 일관성 유지
 *
 * @param liabilityId - 손실 책임 문서 ID
 * @param currentStatus - 현재 상태
 * @param newStatus - 새 상태
 * @param currentHistory - 현재 히스토리 배열
 * @param userId - 변경을 수행한 사용자 ID
 * @returns 업데이트 성공 여부
 */
export const updateIssueLiabilityStatus = async (
  liabilityId: string,
  currentStatus: IssueLiabilityStatus,
  newStatus: IssueLiabilityStatus,
  currentHistory: IssueLiabilityHistoryEntry[] | null | undefined,
  userId: string,
): Promise<boolean> => {
  // 상태가 동일한 경우 업데이트하지 않음
  if (currentStatus === newStatus) {
    return true;
  }

  try {
    const previousStatusName = issueLiabilityStatusMap[currentStatus];
    const newStatusName = issueLiabilityStatusMap[newStatus];

    const historyEntry: IssueLiabilityHistoryEntry = {
      createdAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
      memo: `상태 변경: ${previousStatusName} → ${newStatusName}`,
      createdBy: userId,
      isManual: false,
    };

    const newHistory = [...(currentHistory ?? []), historyEntry];

    await updateIssueLiability(liabilityId, {
      status: newStatus,
      history: newHistory,
    });

    return true;
  } catch (error) {
    console.error('Failed to update issue liability status:', error);
    throw error;
  }
};

/**
 * IssueLiability 업데이트 시 상태 변경이 포함된 경우 히스토리 자동 추가
 * - 일반 업데이트와 상태 변경을 함께 처리
 * - 상태가 변경된 경우에만 히스토리에 기록
 *
 * @param liabilityId - 손실 책임 문서 ID
 * @param updateData - 업데이트할 데이터
 * @param currentLiability - 현재 손실 책임 문서
 * @param userId - 변경을 수행한 사용자 ID
 * @returns 업데이트 성공 여부
 */
export const updateIssueLiabilityWithStatusHistory = async (
  liabilityId: string,
  updateData: Partial<IssueLiability>,
  currentLiability: IssueLiabilityDoc,
  userId: string,
): Promise<boolean> => {
  try {
    const finalUpdateData = { ...updateData };

    // 상태가 변경된 경우 히스토리에 자동 추가
    if (updateData.status && updateData.status !== currentLiability.status) {
      const previousStatusName = issueLiabilityStatusMap[currentLiability.status];
      const newStatusName = issueLiabilityStatusMap[updateData.status];

      const historyEntry: IssueLiabilityHistoryEntry = {
        createdAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
        memo: `상태 변경: ${previousStatusName} → ${newStatusName}`,
        createdBy: userId,
        isManual: false,
      };

      // 기존 history 또는 updateData의 history에 추가
      const baseHistory = updateData.history ?? currentLiability.history ?? [];
      finalUpdateData.history = [...baseHistory, historyEntry];
    }

    await updateIssueLiability(liabilityId, finalUpdateData);
    return true;
  } catch (error) {
    console.error('Failed to update issue liability:', error);
    throw error;
  }
};
